# Loading a pair

Two exports of the **same source**: the run you trust, and the run you are
checking.

- **Baseline** is the last known-good run — GTZenda's parent run for the source.
- **Latest** is the run under alert.

The file order matters, and the app checks it: the export dates are read from
each file and compared, and Analyze is held if the baseline looks newer than the
latest. If you have no files to hand, **Try with sample data** loads two real
runs of one source, a week apart.

## How large a pair this can take

The whole analysis runs in this browser, and the result graph is about the size
of the files that made it — held two or three times over while it is built,
handed to the page, and cached. So the pair's own size decides whether the tab
copes.

A file over 10 MB gets a warning: the run is unusual, not impossible. A pair over
58 MB is **refused**, because a pair twice that size was measured to wedge the
tab with no error and no result — a refusal you can act on beats a spinner that
never ends. For a pair that big, `npm run analyze` does the same analysis outside
the browser.

## A folder of runs at once

**Analyse a folder of runs** takes the day's exports across every source and
compares each source's two newest runs, one at a time, handing back a single zip:
one folder of artifacts per source, plus a summary that accounts for every file.

It decides nothing and records nothing. It answers "which of these runs needs a
person", and you open that pair here.

Three things it refuses rather than guesses: a file no profile recognises (or one
two profiles claim), a source with only one run in the folder, and any group whose
filenames do not carry the pipeline's run timestamps — the order of a pair is what
decides which side is the regression, and nobody is watching each pair in a batch.
Everything refused is named in the panel and in the summary.

## Confirming the source

The source profile is what governs the run — how records are matched, which
fields may be backfilled, when an export is blocked. It is detected from the
bot identity carried in the records themselves, not from the file name.

If nothing matches, the analysis is **held** rather than run under whichever
profile happened to be selected. That is deliberate: another source's backfill
approvals are not yours to borrow. Pick a profile deliberately, or measure a
draft one from the pair in front of you.

A **draft profile** governs reporting and nothing else. It approves no backfill,
sets no match-rate floor, and never releases a recovered export.
